import React from 'react';
import { Column } from 'react-table';
import SessionTable, {
  ResponseGuard,
  SessionTableInstanceProps
} from '../index';
import { YesNoCell, CellFunction, NAGuard, NoneGuard } from '../../cells';
import Tooltip from '../../../tooltip';
import Icon from '../../../../atoms/icon';
import { IEpxAsthmaYZ } from '../../../../../libs/apps/epxasthmayz';
import moment from 'moment-timezone';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';

const getDependencies = (sessions: IEpxAsthmaYZ[]): Dependencies => {
  let shouldRecheck = false;
  for (const session of sessions) {
    const schedule = session.schedule;
    if (schedule?.shouldRecheck === true) {
      shouldRecheck = true;
    }
  }
  return [shouldRecheck];
};

type Dependencies = [boolean];

const GetNACheckRegular = () => {
  return (session: IEpxAsthmaYZ): boolean => {
    return session.isConnect === true;
  };
};

const getColumns = (sessions: IEpxAsthmaYZ[]): Column<IEpxAsthmaYZ>[] => {
  const [shouldRecheck] = getDependencies(sessions);
  const columns: Column<IEpxAsthmaYZ>[] = [
    {
      Header: (
        <Tooltip
          content={'This value may be N/A if the patient texted in CONNECT.'}
        >
          <span>
            Experiencing Symptoms
            <Icon className="ml-2" icon={faInfoCircle} />
          </span>
        </Tooltip>
      ),
      id: 'hasSymptoms',
      Cell: NAGuard(
        ResponseGuard(YesNoCell('hasSymptoms')),
        GetNACheckRegular()
      )
    },
    {
      Header: (
        <Tooltip
          content={'This value may be N/A if the patient texted in CONNECT.'}
        >
          <span>
            Completed Yellow Zone
            <Icon className="ml-2" icon={faInfoCircle} />
          </span>
        </Tooltip>
      ),
      id: 'completedYZ',
      Cell: NAGuard(
        ResponseGuard(YesNoCell('completedYZ')),
        GetNACheckRegular()
      )
    },
    {
      Header: (
        <Tooltip
          content={'This value may be N/A if the patient texted in CONNECT.'}
        >
          <span>
            Symptoms Improved
            <Icon className="ml-2" icon={faInfoCircle} />
          </span>
        </Tooltip>
      ),
      id: 'symptomsImproved',
      Cell: NAGuard(
        ResponseGuard(YesNoCell('symptomsImproved')),
        GetNACheckRegular()
      )
    }
  ];
  if (shouldRecheck) {
    columns.push({
      Header: (
        <Tooltip
          content={
            'This value will only be present if the patient responded to their 24hr recheck. It may be N/A if the patient texted in CONNECT.'
          }
        >
          <span>
            24 Hour Improvement
            <Icon className="ml-2" icon={faInfoCircle} />
          </span>
        </Tooltip>
      ),
      id: 'symptomsImproved24hr',
      Cell: NAGuard(
        ResponseGuard(YesNoCell('symptomsImproved24hr')),
        GetNACheckRegular()
      )
    });
  }
  return columns;
};

const EpxAsthmaYZTable: React.FC<SessionTableInstanceProps> = (
  props
): JSX.Element => {
  return (
    <SessionTable<IEpxAsthmaYZ>
      url="/epxasthmayz/data/"
      getColumns={getColumns}
      getDependencies={getDependencies}
      {...props}
    />
  );
};

export default EpxAsthmaYZTable;

// ACT < 12

const ACTLessThanTwelve = [
  'How is your asthma today?',
  'How much of a problem is your asthma when you run, exercise or play sports?',
  'Do you cough because of your asthma?',
  'Do you wake up during the night because of your asthma?',
  'During the last 4 weeks, how many days did your child have any daytime asthma symptoms?',
  'During the last 4 weeks, how many days did your child wheeze during the day because of asthma?',
  'During the last 4 weeks, how many days did your child wake up during the night because of asthma?'
];

const ACTLessThanTwelveResponseTimeCell = ({
  row: { original: session }
}): CellFunction<IEpxAsthmaYZ> => {
  const answers = session.ACTLessThanTwelveAnswers || {};
  if (!(answers && answers[1] != null)) {
    return <span>Never</span>;
  }
  const value = session.respondedAt as Date;
  const time = moment(value);
  const format = 'MM/DD/YY hh:mm a';
  return NoneGuard(value) || <span>{time.format(format)}</span>;
};

const ACTLessThanTwelveCell = (index: number): CellFunction<IEpxAsthmaYZ> => {
  return ({ row: { original: session } }) => {
    const answers = session.ACTLessThanTwelveAnswers || {};
    const answer = answers[index];
    const GetTooltipFollowUp = (i) => {
      if ([0, 1, 2, 3].includes(i - 1)) {
        return ' Scale: 0 - 3';
      } else if ([4, 5, 6].includes(i - 1)) {
        return ' Scale: 0 - 5';
      }
    };
    return (
      <div className={'font-bold'}>
        {NoneGuard(answer) || (
          <Tooltip
            // different scales for different questions
            content={ACTLessThanTwelve[index - 1] + GetTooltipFollowUp(index)}
          >
            <span>{answer}</span>
          </Tooltip>
        )}
      </div>
    );
  };
};

const ACTLessThanTwelveTotalCell: CellFunction<IEpxAsthmaYZ> = ({
  row: { original: session }
}) => {
  const answers = session.ACTLessThanTwelveAnswers || {};
  let total = 0;
  for (let i = 0; i < 7; i++) {
    if (answers[i + 1] >= 0) {
      total += answers[i + 1];
    }
  }
  return (
    <Tooltip
      content={
        <span>
          ACT {'<'} 12 Interpretation:
          <br />
          {'<'}13: Very poorly controlled asthma
          <br />
          {'>='}13 or {'<='}19: Poorly controlled asthma
          <br />
          {'>'}19: Well controlled asthma
        </span>
      }
    >
      <b>{total}</b>
    </Tooltip>
  );
};

const getColumnsACTLesser = (): Column<IEpxAsthmaYZ>[] => {
  const columns: Column<IEpxAsthmaYZ>[] = [
    {
      Header: 'Survey Number',
      accessor: 'ACTLesserSerial',
      Cell: ({ cell: { value } }) => {
        return <b>#{value + 1}</b>;
      }
    },
    {
      Header: 'Last Response At',
      id: 'lastResponseAt',
      Cell: ACTLessThanTwelveResponseTimeCell as CellFunction<IEpxAsthmaYZ>
    }
  ];
  columns.push({
    Header: 'ACT < 12 Total',
    id: 'total',
    Cell: ACTLessThanTwelveTotalCell
  });
  for (let i = 0; i < 7; i++) {
    columns.push({
      Header: ACTLessThanTwelve[i],
      id: `q${i + 1}`,
      Cell: ACTLessThanTwelveCell(i + 1)
    });
  }
  return columns;
};

export const ACTLessThanTwelveTable: React.FC<SessionTableInstanceProps> = (
  props
): JSX.Element => {
  return (
    <SessionTable<IEpxAsthmaYZ>
      url="/epxasthmayz/data_act_lesser/"
      type="vertical"
      getColumns={getColumnsACTLesser}
      disableDefaultColumns
      {...props}
      striped={false}
      bareCell
    />
  );
};

// ACT > 12

const ACTGreaterThanTwelve = [
  'In the past 4 weeks, how much of the time did your asthma keep you from getting as much done at work, school or at home? Reply with a number from 1 to 5:',
  'During the past 4 weeks, how often have you had shortness of breath? Reply with a number from 1 to 5:',
  'During the past 4 weeks, how often did your asthma symptoms wake you up at night or earlier than usual in the morning? Reply with a number from 1 to 5:',
  'During the past 4 weeks, how often have you used your rescue inhaler or nebulizer medication (such as albuterol)? Reply with a number from 1 to 5:',
  'How would you rate your asthma control during the past 4 weeks? Reply with a number from 1 to 5:'
];

const ACTGreaterThanTwelveResponseTimeCell = ({
  row: { original: session }
}): CellFunction<IEpxAsthmaYZ> => {
  const answers = session.ACTGreaterThanTwelveAnswers || {};
  if (!(answers && answers[1] != null)) {
    return <span>Never</span>;
  }
  const value = session.respondedAt as Date;
  const time = moment(value);
  const format = 'MM/DD/YY hh:mm a';
  return NoneGuard(value) || <span>{time.format(format)}</span>;
};

const ACTGreaterThanTwelveCell = (
  index: number
): CellFunction<IEpxAsthmaYZ> => {
  return ({ row: { original: session } }) => {
    const answers = session.ACTGreaterThanTwelveAnswers || {};
    const answer = answers[index];
    return (
      <div className={'font-bold'}>
        {NoneGuard(answer) || (
          <Tooltip content={ACTGreaterThanTwelve[index - 1] + ' Scale: 1 - 5'}>
            <span>{answer}</span>
          </Tooltip>
        )}
      </div>
    );
  };
};

const ACTGreaterThanTwelveTotalCell: CellFunction<IEpxAsthmaYZ> = ({
  row: { original: session }
}) => {
  const answers = session.ACTGreaterThanTwelveAnswers || {};
  let total = 0;
  for (let i = 0; i < 5; i++) {
    if (answers[i + 1] >= 0) {
      total += answers[i + 1];
    }
  }
  return (
    <Tooltip
      content={
        <span>
          ACT {'>'} 12 Interpretation:
          <br />
          {'<'}16: Very poorly controlled asthma
          <br />
          {'>='}16 or {'<='}19: Poorly controlled asthma
          <br />
          {'>'}19: Well controlled asthma
        </span>
      }
    >
      <b>{total}</b>
    </Tooltip>
  );
};

const getColumnsACTGreater = (): Column<IEpxAsthmaYZ>[] => {
  const columns: Column<IEpxAsthmaYZ>[] = [
    {
      Header: 'Survey Number',
      accessor: 'ACTGreaterSerial',
      Cell: ({ cell: { value } }) => {
        return <b>#{value + 1}</b>;
      }
    },
    {
      Header: 'Last Response At',
      id: 'lastResponseAt',
      Cell: ACTGreaterThanTwelveResponseTimeCell as CellFunction<IEpxAsthmaYZ>
    }
  ];
  columns.push({
    Header: 'ACT > 12 Total',
    id: 'total',
    Cell: ACTGreaterThanTwelveTotalCell
  });
  for (let i = 0; i < 5; i++) {
    columns.push({
      Header: ACTGreaterThanTwelve[i],
      id: `q${i + 1}`,
      Cell: ACTGreaterThanTwelveCell(i + 1)
    });
  }
  return columns;
};

export const ACTGreaterThanTwelveTable: React.FC<SessionTableInstanceProps> = (
  props
): JSX.Element => {
  return (
    <SessionTable<IEpxAsthmaYZ>
      url="/epxasthmayz/data_act_greater/"
      type="vertical"
      getColumns={getColumnsACTGreater}
      disableDefaultColumns
      {...props}
      striped={false}
      bareCell
    />
  );
};
